import { usePathname, useSearchParams } from 'next/navigation'
import { ComponentProps, useCallback, useMemo } from 'react'
import reactStringReplace from 'react-string-replace'

import { Bag as BagIcon, Clock, Media, ParcelOutline } from '@syconium/little-miss-figgy'

import { ProductCatalogVariantGroup } from '../../../../../__generated__/graphql/catalog/graphql'
import { useCart } from '../../../../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { usePortal } from '../../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { IntlShippingAndTaxMsg } from '../../../../../brunswick/components/IntlShippingAndTaxMsg'
import { ProductCategories } from '../../../../../brunswick/constants/product'
import { RefsContainer } from '../../../../../brunswick/containers/refs'
import { EmbroideryService } from '../../../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../../../containers/EmbroideryService/EmbroideryModalService'
import { ColorGroup, ProductAsset } from '../../../../../types/graphql/product'
import { CartErrorMessage } from '../../../../atoms/CartErrorMessage/CartErrorMessage'
import { EmbroideryModal } from '../../../../embroidery/EmbroideryModal/EmbroideryModal'
import { CompleteTheSet } from '../CompleteTheSet/CompleteTheSet'
import { AddToBagContainer } from '../contexts/AddToBagContainer'
import { ProductDetailsContainer } from '../contexts/ProductDetailsContainer'
import { usePortalEmbroiderySettings } from '../hooks/usePortalEmbroiderySettings'
import { useProductPageEmbroiderySettings } from '../hooks/useProductPageEmbroiderySettings'
import { useProductPagePortal } from '../hooks/useProductPagePortal'
import { ProductHighlights } from '../ProductHighlights/ProductHighlights'
import { ProductPageTemplateProps } from '../ProductPageTemplate'
import { WaitlistModalProvider } from '../WaitlistModal/WaitlistModal'
import WhyWeLove from '../WhyWeLoveSection/WhyWeLoveSection'

import { AddToBagButton } from './AddToBagButton'
import { CollectionLinks } from './CollectionLink/CollectionLink'
import { EmbroiderySection } from './EmbroiderySection'
import { ColorSection } from './GarmentAttributes/ColorSection'
import { FitsSection } from './GarmentAttributes/FitsSection'
import { SizesSection } from './GarmentAttributes/SizesSection'
import { ProductDetailsAccordionSection } from './ProductDetailsAccordionSection/ProductDetailsAccordionSection'
import * as Styles from './StandardProductDetails.styles'
import { StickyAddToBagModal } from './StickyAddToBagModal'
import * as SharedStyles from './styles'

type StandardProductDetailsProps = {
  product: ComponentProps<typeof CompleteTheSet>['product']
  variantGroup: ComponentProps<typeof CompleteTheSet>['variantGroup'] &
    Pick<
      ProductCatalogVariantGroup,
      | 'associatedCollectionLinks'
      | 'fitModelsDescriptions'
      | 'careInstructions'
      | 'features'
      | 'materials'
    >
  /** @deprecated */
  deprecatedProps: {
    /** @deprecated */
    featuredProduct: ProductPageTemplateProps['featuredProduct']
    /** @deprecated */
    productGroup: ProductPageTemplateProps['productGroup']
    /** @deprecated */
    selectedVariant: ProductPageTemplateProps['selectedVariant']
  }
}

export const StandardProductDetails = (props: StandardProductDetailsProps) => {
  return (
    <DeprecatedStandardProductDetailsProviders {...props}>
      <StandardProductDetailsImpl {...props} />
    </DeprecatedStandardProductDetailsProviders>
  )
}

const DeprecatedStandardProductDetailsProviders = ({
  deprecatedProps: { featuredProduct, productGroup, selectedVariant },
  children,
}: StandardProductDetailsProps & { children?: React.ReactNode }) => {
  const { hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery, portal } = usePortal()
  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery({
    categoryAnalyticsId: productGroup.category,
  })
  const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery({
    categoryAnalyticsId: productGroup.category,
  })
  const {
    embroidery: { discountText },
  } = useTranslation()

  const currentColorKey = featuredProduct.colorInfo?.rawName ?? ''
  const currentProductColor = useMemo(() => {
    const colors = productGroup.colorGroups.flatMap(o => o.colors) ?? []
    return colors.find(o => o.rawName === currentColorKey)
  }, [currentColorKey, productGroup])

  // Portal-specific settings related to the current product/product group
  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups: productGroup.portalColorGroups ?? [],
    selectedColorKey: currentColorKey,
  })

  // Embroidery settings related to the current product/product group
  const { embroideryOptions } = useProductPageEmbroiderySettings({
    embroidery: featuredProduct.embroidery,
    mandatoryLogoEmbroidery,
    isPortalColorSelected,
  })

  // The discount message to display at the bottom of the embroidery modal. Currently displayed only on portal-applicable PDP's.
  const embroideryDiscountMessage = useMemo(() => {
    const portalDiscountPercentage = portal?.embroidery?.embroideryDiscountPercentage

    // Only display portal discount messaging if the portal-specific logo embroidery option is present
    const hasPortalEmbroideryOptions = embroideryOptions.some(({ kind }) => kind === 'LOGO_ONLY')
    if (!portalDiscountPercentage || !hasPortalEmbroideryOptions) return undefined

    return discountText.formSection.replace(
      '{{discountPercentage}}',
      portalDiscountPercentage.toString()
    )
  }, [
    discountText.formSection,
    embroideryOptions,
    portal?.embroidery?.embroideryDiscountPercentage,
  ])

  // Embroidery settings related to the user's current portal
  const {
    hiddenEmbroideryFields,
    defaultInitialFormValues,
    defaultSelectedEmbroideryOptions,
    textEmbroiderySecondLineOptions,
    requiredEmbroideryOptions,
  } = usePortalEmbroiderySettings({
    embroideryOptions,
    mandatoryLogoEmbroidery,
    mandatoryTextEmbroidery,
    isPortalColorSelected,
  })

  const productImagesWithDefaultFlatlays: ProductAsset[] = useMemo(() => {
    return (featuredProduct.images?.nodes ?? []).concat(productGroup.embroideryImages ?? [])
  }, [featuredProduct, productGroup])

  return (
    <WaitlistModalProvider
      colorDisplayValue={currentProductColor?.name ?? currentColorKey}
      productTitle={productGroup.title}
      variantShopifyId={selectedVariant?.shopifyId}
    >
      <EmbroideryService.Provider
        initialState={{
          hideFields: hiddenEmbroideryFields,
          defaultInitialFormValues,
          defaultSelectedEmbroideryOptions,
          embroideryOptions,
          productImagesWithDefaultFlatlays,
          productColor: currentProductColor,
          categoryAnalyticsId: productGroup.category,
          selectedSize: selectedVariant ? selectedVariant.size : null,
          textEmbroideryOptions: {
            secondLine: textEmbroiderySecondLineOptions,
          },
        }}
        key={`${productGroup.handle}`}
      >
        <EmbroideryModalService.Provider
          initialState={{
            discountMessage: embroideryDiscountMessage,
            requiredEmbroideryOptions,
          }}
        >
          <EmbroideryModal />
          <ProductDetailsContainer.Provider
            state={{
              featuredProduct,
              productGroup,
              selectedVariant,
            }}
          >
            <AddToBagContainer.Provider>{children}</AddToBagContainer.Provider>
          </ProductDetailsContainer.Provider>
        </EmbroideryModalService.Provider>
      </EmbroideryService.Provider>
    </WaitlistModalProvider>
  )
}

const StandardProductDetailsImpl = ({ product, variantGroup }: StandardProductDetailsProps) => {
  const {
    detailsSection: {
      freeShippingMessage,
      freeShippingMessageBold,
      freeShippingAndReturnsMessage,
      freeShippingAndReturnsMessageBold,
      portalPromoText,
    },
    pages: { product: productPageStrings },
  } = useTranslation()

  const {
    featuredProduct,
    productGroup,
    selectedVariant,
    isProductOnWaitlist,
    isProductFinalSale,
    wwltImage,
    selectedSize,
    isProductEmbroiderable,
    colorGroups,
    portalColorGroups,
  } = ProductDetailsContainer.useContainer()

  // Portal-specific settings related to the current product/product group
  const currentColorKey = featuredProduct.colorInfo?.rawName ?? ''
  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups: productGroup.portalColorGroups ?? [],
    selectedColorKey: currentColorKey,
  })

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { portal } = usePortal()
  const { swPromoEligible } = featuredProduct
  const { detailsSectionAddToBagSectionRef } = RefsContainer.useContainer()
  const {
    resetInvalidBagClickStatus,
    addToBagErrors,
    addToBagCta,
    onAddToBagClick,
    isAddToCartInFlight,
    shouldShowSelectSizeCta,
  } = AddToBagContainer.useContainer()
  const { errorMessage } = useCart()
  const { isUSRegion } = useLocalization()

  const isKit = productGroup.rawCategory === ProductCategories.KIT

  const addToBagCtaIconTestDecision = useFeatureFlag<
    'atb-cta-bag-icon-test-control' | 'atb-cta-bag-icon-test-variant'
  >({
    key: 'atb-cta-bag-icon-test',
    controlVariant: 'atb-cta-bag-icon-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: isProductOnWaitlist,
  })

  const freeReturnsMessagingDecision = useFeatureFlag<'control' | 'icon-bold' | 'background-color'>(
    {
      key: 'free-returns-messaging',
      controlVariant: 'control',
      ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
      skip: !isUSRegion,
    }
  )

  const isVariant = freeReturnsMessagingDecision !== 'control'

  // Parse strings and replace <strong> tags with BoldText component
  const parseBoldMarkup = (text: string) =>
    reactStringReplace(text, /<strong>(.*?)<\/strong>/g, (match, i) => (
      <SharedStyles.BoldText key={i}>{match}</SharedStyles.BoldText>
    ))

  const experimentShippingText = isVariant
    ? parseBoldMarkup(freeShippingMessageBold)
    : freeShippingMessage

  const experimentFreeShippingAndReturnsText = isVariant
    ? parseBoldMarkup(freeShippingAndReturnsMessageBold)
    : freeShippingAndReturnsMessage

  const isIconVisible =
    addToBagCtaIconTestDecision === 'atb-cta-bag-icon-test-variant' &&
    !isAddToCartInFlight &&
    !isProductOnWaitlist &&
    !shouldShowSelectSizeCta

  const featuredColorKey = featuredProduct.colorInfo?.rawName ?? selectedVariant?.rawColor
  const featuredFitKey = featuredProduct.rawFit ?? selectedVariant?.rawFit
  const selectedVariantHasLowInventory = Boolean(selectedVariant?.isLowInventory)

  // Generate the URL to use for a given tab
  // This will navigate the user to the current PDP url with the first color in the given color group selected
  const generateTabLink = useCallback(
    (cgs: ColorGroup[]) => {
      const colorKey = cgs[0]?.colors?.[0]?.rawName
      const search = new URLSearchParams(searchParams?.toString() ?? '')
      if (colorKey) search.set('color', colorKey)

      return {
        pathname,
        search: search.toString(),
      }
    },
    [searchParams, pathname]
  )

  const activeColorGroups = isPortalColorSelected ? portalColorGroups : colorGroups

  return (
    <div>
      {/* Pricing and badges */}
      <ProductHighlights />

      {/* Conditional portals tabs and content. */}
      {portalColorGroups.length > 0 ? (
        <Styles.TabContainer>
          {colorGroups.length > 0 && (
            <Styles.Tab
              aria-label='Show FIGS offerings'
              aria-current={!isPortalColorSelected}
              href={generateTabLink(colorGroups) ?? ''}
              scroll={false}
            >
              <Styles.TabText>FIGS</Styles.TabText>
            </Styles.Tab>
          )}
          {portalColorGroups.length > 0 && (
            <Styles.Tab
              aria-label='Show your organizations offerings'
              aria-current={isPortalColorSelected}
              href={generateTabLink(portalColorGroups) ?? ''}
              scroll={false}
            >
              <Styles.TabText>My Uniform</Styles.TabText>
            </Styles.Tab>
          )}
        </Styles.TabContainer>
      ) : null}
      {portal?.discountPercentage ? (
        <Styles.PortalPromoText data-testid='portal-promo-text'>{`${portal.discountPercentage}${portalPromoText}`}</Styles.PortalPromoText>
      ) : null}

      {/* Render the attribute selectors for the current product */}
      <ColorSection
        colorGroups={activeColorGroups}
        allowPriceBasedGrouping={portalColorGroups.length === 0}
        retainColorGroupLabel={isPortalColorSelected}
      />
      {selectedVariantHasLowInventory && !selectedVariant?.soldOut ? (
        <Styles.LowInventoryTypography>
          <Clock />
          {productPageStrings.lowInventoryWarning}
        </Styles.LowInventoryTypography>
      ) : null}
      <SizesSection />
      <FitsSection />

      {/* Embroidery options */}
      <EmbroiderySection
        isPortalColorSelected={isPortalColorSelected}
        embroiderable={isProductEmbroiderable && !isProductOnWaitlist}
        resetInvalidBagClickStatus={resetInvalidBagClickStatus}
        addToBagErrors={addToBagErrors}
      />

      {/* Add to bag button */}
      <SharedStyles.AddToBagButtonContainer ref={detailsSectionAddToBagSectionRef}>
        <AddToBagButton
          onClickAddToBag={onAddToBagClick}
          isProcessing={isAddToCartInFlight}
          isWaitlist={isProductOnWaitlist}
          cta={addToBagCta}
          icon={isIconVisible && <BagIcon height={26} width={24} />}
        />
        {featuredColorKey && featuredFitKey && !isKit ? (
          <Styles.PDPFavoritesButtonContainer>
            <Styles.PDPFavoritesButton
              productGroupHandle={productGroup.handle}
              sizeKey={selectedSize?.shorthand}
              colorKey={featuredColorKey}
              fitKey={featuredFitKey}
              productGroupImageSrc={featuredProduct.images?.nodes[0]?.source}
              data-heap-category='Favorites (PDP)'
            />
          </Styles.PDPFavoritesButtonContainer>
        ) : null}
      </SharedStyles.AddToBagButtonContainer>
      {errorMessage && (
        <Styles.ErrorMessageContainer>
          <CartErrorMessage errorMessage={errorMessage} scrollToError />
        </Styles.ErrorMessageContainer>
      )}
      <StickyAddToBagModal>
        <AddToBagButton
          onClickAddToBag={onAddToBagClick}
          icon={isIconVisible && <BagIcon height={26} width={24} />}
          isProcessing={isAddToCartInFlight}
          isWaitlist={isProductOnWaitlist}
          cta={addToBagCta}
        />
      </StickyAddToBagModal>

      {/* Display shipping/tax information for the current locale */}
      <Styles.IntlMsgWrap>
        <IntlShippingAndTaxMsg />
      </Styles.IntlMsgWrap>

      {/* Promo exclusion messaging */}
      {!swPromoEligible && (
        <Styles.PromoExclusionMessage>
          {productPageStrings.notEligibleForDiscount}
        </Styles.PromoExclusionMessage>
      )}

      {/* Free shipping messaging for domestic customers */}
      {isUSRegion && (
        <SharedStyles.ProductDetailsCopy style={{ margin: '24px 0', textAlign: 'center' }}>
          <Styles.ProductDetailsTitle
            $hasBackgroundColor={freeReturnsMessagingDecision === 'background-color'}
            $hasIcon={isVariant}
            isUppercase={!isVariant}
            textTransform={!isVariant ? 'uppercase' : undefined}
          >
            <span>
              {isProductFinalSale ? experimentShippingText : experimentFreeShippingAndReturnsText}
            </span>
            {isVariant && <ParcelOutline />}
          </Styles.ProductDetailsTitle>
        </SharedStyles.ProductDetailsCopy>
      )}

      {!isKit && (
        <>
          <Media lessThan='md'>
            <Styles.WhyWeLoveDivider />
          </Media>
          <CompleteTheSet product={product} variantGroup={variantGroup} />
        </>
      )}

      {/* WWL mobile section */}
      <Media lessThan='md'>
        <>
          <Styles.WhyWeLoveDivider />
          <WhyWeLove
            imageSrc={wwltImage?.source}
            whyWeLoveText={featuredProduct.whyWeLove}
            whyWeLoveIcons={featuredProduct.iconList}
          />
        </>
      </Media>

      {/* WWL desktop section */}
      <Media greaterThanOrEqual='md'>
        <Styles.WhyWeLoveText>{featuredProduct.whyWeLove}</Styles.WhyWeLoveText>
      </Media>

      {/* In depth product details and info. */}
      <ProductDetailsAccordionSection
        careInstructions={variantGroup.careInstructions}
        fitModelsDescriptions={variantGroup.fitModelsDescriptions}
        features={variantGroup.features}
        materials={variantGroup.materials}
      />

      {/* Pill style links to associated collections. */}
      {variantGroup.associatedCollectionLinks ? (
        <CollectionLinks links={variantGroup.associatedCollectionLinks} />
      ) : null}
    </div>
  )
}
